/**
 * GA4 event catalog — edit this file to rename, disable, or retarget events.
 *
 * Add a new event:
 *   1. Add a row to GA_EVENTS (name, category, enabled).
 *   2. Add a payload type on GaEventPayloadsMap.
 *   3. Call track('your_event', payload) at the source.
 *
 * Disable without touching call sites: set enabled: false.
 * Rename what GA receives: change `name` only; keep the key stable.
 */

export type GaCategory =
  | 'Authentication'
  | 'Lobby'
  | 'Game'
  | 'Audio'
  | 'Screen';

export type GaScreen = 'login' | 'lobby' | 'game';

export type GaAudioChannel = 'sfx' | 'music' | 'all';

type GaEventDef = {
  name: string;
  category: GaCategory;
  enabled: boolean;
};

export const GA_EVENTS = {
  login_attempt: {
    name: 'login_attempt',
    category: 'Authentication',
    enabled: true,
  },
  login_success: {
    name: 'login_success',
    category: 'Authentication',
    enabled: true,
  },
  login_failure: {
    name: 'login_failure',
    category: 'Authentication',
    enabled: true,
  },
  demo_start: {
    name: 'demo_start',
    category: 'Authentication',
    enabled: true,
  },
  demo_success: {
    name: 'demo_success',
    category: 'Authentication',
    enabled: true,
  },
  demo_failure: {
    name: 'demo_failure',
    category: 'Authentication',
    enabled: true,
  },
  logout: {
    name: 'logout',
    category: 'Authentication',
    enabled: true,
  },
  game_select: {
    name: 'game_select',
    category: 'Lobby',
    enabled: true,
  },
  screen_view: {
    name: 'page_view',
    category: 'Screen',
    enabled: true,
  },
  intro_closed: {
    name: 'intro_closed',
    category: 'Game',
    enabled: true,
  },
  sound_muted: {
    name: 'sound_muted',
    category: 'Audio',
    enabled: true,
  },
  sound_unmuted: {
    name: 'sound_unmuted',
    category: 'Audio',
    enabled: true,
  },
  spin_clicked: {
    name: 'spin_clicked',
    category: 'Game',
    enabled: true,
  },
  autospin_clicked: {
    name: 'autospin_clicked',
    category: 'Game',
    enabled: true,
  },
  kick_clicked: {
    name: 'kick_clicked',
    category: 'Game',
    enabled: true,
  },
  win: {
    name: 'win',
    category: 'Game',
    enabled: true,
  },
  loss: {
    name: 'loss',
    category: 'Game',
    enabled: true,
  },
  bet_placed: {
    name: 'bet_placed',
    category: 'Game',
    enabled: true,
  },
  cashout: {
    name: 'cashout',
    category: 'Game',
    enabled: true,
  },
  crashed: {
    name: 'crashed',
    category: 'Game',
    enabled: true,
  },
  autoplay_started: {
    name: 'autoplay_started',
    category: 'Game',
    enabled: true,
  },
  autoplay_stopped: {
    name: 'autoplay_stopped',
    category: 'Game',
    enabled: true,
  },
} as const satisfies Record<string, GaEventDef>;

export type GaEventName = keyof typeof GA_EVENTS;

/** Compile-time check: every catalog event has a payload type. */
export type GaEventPayloads = { [K in GaEventName]: GaEventPayloadsMap[K] };

type GaEventPayloadsMap = {
  login_attempt: { method?: string };
  login_success: { method?: string };
  login_failure: { reason?: string };
  demo_start: Record<string, never>;
  demo_success: Record<string, never>;
  demo_failure: { reason?: string };
  logout: Record<string, never>;
  game_select: { game_slug: string };
  screen_view: { screen: GaScreen };
  intro_closed: Record<string, never>;
  sound_muted: { channel: GaAudioChannel };
  sound_unmuted: { channel: GaAudioChannel };
  spin_clicked: { bet_amount?: number; side?: string };
  autospin_clicked: { enabled: boolean; side?: string };
  kick_clicked: { bet_amount?: number };
  win: {
    value: number;
    multiplier?: number;
    bet_amount?: number;
    side?: string;
  };
  loss: {
    value?: number;
    multiplier?: number;
    bet_amount?: number;
    side?: string;
  };
  bet_placed: { bet_amount?: number; side?: string };
  cashout: { value?: number; multiplier?: number; side?: string };
  crashed: { multiplier?: number };
  autoplay_started: { side?: string };
  autoplay_stopped: { side?: string };
};
